import React, { useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const SubnetCalculatorPage = () => {
  const [ipAddress, setIpAddress] = useState('');
  const [cidr, setCidr] = useState('24');
  const [result, setResult] = useState(null);

  const calculate = () => {
    const parts = ipAddress.split('.').map(Number);
    if (parts.length !== 4 || parts.some(p => isNaN(p) || p < 0 || p > 255)) {
      alert('Please enter a valid IP address');
      return;
    }

    const cidrNum = parseInt(cidr);
    if (cidrNum < 0 || cidrNum > 32) {
      alert('CIDR must be between 0 and 32');
      return;
    }

    // Calculate subnet mask
    const maskBinary = '1'.repeat(cidrNum) + '0'.repeat(32 - cidrNum);
    const subnetMask = [
      parseInt(maskBinary.slice(0, 8), 2),
      parseInt(maskBinary.slice(8, 16), 2),
      parseInt(maskBinary.slice(16, 24), 2),
      parseInt(maskBinary.slice(24, 32), 2)
    ];

    // Calculate network address
    const networkAddress = parts.map((p, i) => p & subnetMask[i]);

    // Calculate broadcast address
    const wildcardMask = subnetMask.map(m => 255 - m);
    const broadcastAddress = networkAddress.map((n, i) => n | wildcardMask[i]);

    // Calculate host range
    const firstHost = [...networkAddress];
    firstHost[3] += 1;
    const lastHost = [...broadcastAddress];
    lastHost[3] -= 1;

    // Calculate number of hosts
    const totalHosts = Math.pow(2, 32 - cidrNum);
    const usableHosts = cidrNum < 31 ? totalHosts - 2 : (cidrNum === 31 ? 2 : 1);

    setResult({
      subnetMask: subnetMask.join('.'),
      networkAddress: networkAddress.join('.'),
      broadcastAddress: broadcastAddress.join('.'),
      firstHost: firstHost.join('.'),
      lastHost: lastHost.join('.'),
      totalHosts,
      usableHosts,
      wildcardMask: wildcardMask.join('.')
    });
  };

  return (
    <div className="min-h-screen bg-[#0d0d0d] flex flex-col">
      <Header />
      <main className="flex-1">
        <div className="max-w-3xl mx-auto px-4 py-8">
          <nav className="text-sm text-gray-500 mb-4">
            <Link to="/home" className="text-cyan-400 hover:text-cyan-300">Home</Link>
            <span className="mx-2">›</span>
            <span className="text-gray-400">Subnet Calculator</span>
          </nav>
          <h1 className="text-3xl font-bold text-white mb-2">Subnet Calculator</h1>
          <p className="text-gray-400 mb-8">Calculate subnet details from an IP address and CIDR notation.</p>

          <div className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-6">
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-gray-300 mb-2 font-medium">IP Address</label>
                <Input type="text" value={ipAddress} onChange={(e) => setIpAddress(e.target.value)} className="w-full bg-[#0d0d0d] border-[#2a2a2a] text-white" placeholder="192.168.1.1" />
              </div>
              <div>
                <label className="block text-gray-300 mb-2 font-medium">CIDR (Prefix Length)</label>
                <div className="flex gap-2 items-center">
                  <span className="text-gray-400">/</span>
                  <Input type="number" value={cidr} onChange={(e) => setCidr(e.target.value)} className="w-20 bg-[#0d0d0d] border-[#2a2a2a] text-white" min="0" max="32" />
                </div>
              </div>
            </div>
            <Button onClick={calculate} className="mt-5 bg-cyan-600 hover:bg-cyan-500 text-white">Calculate</Button>
            {result && (
              <div className="mt-6 p-5 bg-[#0d0d0d] border border-[#2a2a2a] rounded-xl">
                <div className="space-y-3">
                  <div className="flex justify-between p-3 bg-[#1a1a1a] rounded"><span className="text-gray-400">Network Address:</span><span className="text-cyan-400 font-mono">{result.networkAddress}</span></div>
                  <div className="flex justify-between p-3 bg-[#1a1a1a] rounded"><span className="text-gray-400">Subnet Mask:</span><span className="text-white font-mono">{result.subnetMask}</span></div>
                  <div className="flex justify-between p-3 bg-[#1a1a1a] rounded"><span className="text-gray-400">Wildcard Mask:</span><span className="text-white font-mono">{result.wildcardMask}</span></div>
                  <div className="flex justify-between p-3 bg-[#1a1a1a] rounded"><span className="text-gray-400">Broadcast Address:</span><span className="text-white font-mono">{result.broadcastAddress}</span></div>
                  <div className="flex justify-between p-3 bg-[#1a1a1a] rounded"><span className="text-gray-400">First Host:</span><span className="text-green-400 font-mono">{result.firstHost}</span></div>
                  <div className="flex justify-between p-3 bg-[#1a1a1a] rounded"><span className="text-gray-400">Last Host:</span><span className="text-green-400 font-mono">{result.lastHost}</span></div>
                  <div className="flex justify-between p-3 bg-[#1a1a1a] rounded"><span className="text-gray-400">Usable Hosts:</span><span className="text-white font-bold">{result.usableHosts.toLocaleString()}</span></div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default SubnetCalculatorPage;
